<template>
  <div class="overview-page">
    <div class="line-separator" />
    <RexSpinV3 :spinning="isLoading">
      <div class="overview-header">
        <AWSHeader
          @handle-subscription-select="handleSubscriptionSelect"
          :subscriptions="subscriptions"
          :data="searchSelectOptions"
          :isShowSelectDb="true"
          @dbTypeChanged="handleDbTypeChanged"
        />
      </div>

      <h4 class="overview-text">
        {{ $t('aws.overviewHeading') }}
      </h4>

      <div class="overview-tiles">
        <AWSTile :tiles="tilesData" />

        <AWSAlertTable
          :toggleOptions="alertTableToggleOptions"
          :alertsData="alertTableItems"
          :totalAlertsData="totalAlertsData"
          alertType="subscription"
        />
      </div>
      <div class="overview-subscription-table">
        <AWSTable
          :placeholder="$t('aws.searchSubscriptionId')"
          :title="$t('aws.subscriptionTable')"
          :headers="subscriptionOverviewHeader"
          :data="filteredSubscriptionMatrices"
          @subscriptionClicked="handleSubscriptionClick"
        />
      </div>
    </RexSpinV3>
  </div>
</template>
<script>
import AWSTile from '@/components/Cloud/Dashboard/Components/aws-tiles.vue';
import AWSHeader from '@/components/Cloud/Dashboard/Components/aws-overview-header.vue';
import AWSTable from '@/components/Cloud/Dashboard/Components/aws-table.vue';
import AWSAlertTable from '@/components/Cloud/Dashboard/Components/aws-alert-table.vue';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import {
  alertTableToggleOptions,
  alertsMapper,
  subscriptionOverviewHeader,
  overviewSearchSelectOptions,
} from '@/components/Cloud/Dashboard/cloud.types';
import RexSpinV3 from '@/rex-components/RexSpinV3.vue';
import { permissionTypes } from '@/router/constant';

export default {
  components: {
    AWSHeader,
    AWSTile,
    AWSTable,
    RexSpinV3,
    AWSAlertTable,
  },
  name: 'OverviewPage',
  data() {
    return {
      subscriptions: [],
      subscriptionMatrices: [],
      filteredSubscriptionMatrices: [],
      searchSelectOptions: overviewSearchSelectOptions,
      subType: this.currentSubType,
      tilesData: [],
      alertTableItems: [],
      totalAlertsData: [],
      isLoading: false,
    };
  },
  methods: {
    ...mapActions('cloudState/detail', [
      'fetchAwsServices',
      'fetchAwsServiceInstances',
      'fetchReadMatricDataForServices',
      'getTopAlertsPerService',
      'getTopAlertsPerInstances',
    ]),
    ...mapMutations('cloudState/detail', [
      'setCurrentPage',
      'setCurrentService',
      'setCurrentSubType',
      'setCurrentServices',
    ]),
    async handleDbTypeChanged(value) {
      this.subType = value;
      this.setCurrentSubType(value);
      await this.getInitialData({ isInitialRender: true });
    },
    handleSubscriptionSelect(selectedSubscriptions) {
      if (selectedSubscriptions.length > 0) {
        this.filteredSubscriptionMatrices = this.subscriptionMatrices
          .filter((matrices) =>
            selectedSubscriptions.includes(matrices.subscriptionId),
          )
          .slice(0, 5);

        this.setCurrentServices(
          this.subscriptions
            .filter((item) => selectedSubscriptions.includes(item.label))
            .map((item) => item.key)
            .slice(0, 5),
        );
      } else {
        this.filteredSubscriptionMatrices = [];
      }
    },
    handleSubscriptionClick(subscriptionId) {
      const service = this.$route.params.service;
      const serviceId = this.subscriptions.find(
        (service) => service.label === subscriptionId,
      ).key;
      this.setCurrentService(serviceId);
      this.$router.push({
        name: 'subscriptionId',
        params: { subscriptionId: subscriptionId, serviceId, service },
      });
    },
    async getInitialData(params) {
      const { isInitialRender } = params ?? {};
      try {
        this.isLoading = true;
        const services = await this.fetchAwsServices({
          duration: this.getDurationTime,
          serviceReq: { capability: 'C', subType: this.currentSubType },
          permissions: [permissionTypes.READ, permissionTypes.WRITE],
        });

        this.subscriptions = services.map((service) => ({
          ...service,
          selected: false,
        }));

        this.setCurrentServices(
          isInitialRender
            ? this.subscriptions.map((item) => item.key).slice(0, 5)
            : this.subscriptions
                .filter((subscription) =>
                  this.getCurrentServices.includes(subscription.key),
                )
                .map((item) => item.key),
        );
        const promises = [
          this.getMatricesData(this.getDurationTime),
          this.getServiceLevelTopAlert(this.getDurationTime),
        ];
        await Promise.allSettled(promises);

        await this.getTopAlertsByMetrices(this.subscriptions);
      } catch (error) {
        console.error('Error in beforeMount:', error);
      } finally {
        this.isLoading = false;
      }
    },
    async handleFilterSubscriptionMatrices(currentValue) {
      try {
        this.tilesData = [
          {
            title: this.$t('total-db-instances'),
            content: currentValue.reduce(
              (accumulator, current) => current.noOfDbInstance + accumulator,
              0,
            ),
            tooltip: this.$t('aws.rdsOverviewScreen.tooltips.noOfDbInstance'),
          },
          ...subscriptionOverviewHeader()
            .slice(0, 5)
            .map((header) => {
              const key = header.key.toLowerCase();
              const isAverage = header.valueType === 'avg';

              const content = currentValue.reduce(
                (accumulator, currValue) => {
                  const valueToAdd = isAverage
                    ? currValue[key] / currentValue.length
                    : currValue[key];

                  if (typeof accumulator === 'object') {
                    if (isAverage) {
                      accumulator[key] = (accumulator[key] || 0) + valueToAdd;
                    } else {
                      accumulator[key] =
                        (accumulator[key] || 0) + currValue[key];
                    }
                  } else {
                    accumulator += valueToAdd;
                  }

                  return accumulator;
                },
                isAverage ? {} : 0,
              );

              return {
                title: header.name,
                content:
                  typeof content === 'object'
                    ? content[key]?.toFixed(2)
                    : content?.toFixed(2),
                tooltip: header.tooltip,
              };
            }),
        ];
      } catch (error) {
        console.error('Error in filteredSubscriptionMatrices watcher:', error);
        // Optionally, handle or log the error further as needed
      }
    },
    async getMatricesData(currentDurationTime) {
      const matrices = await this.fetchReadMatricDataForServices({
        headers: subscriptionOverviewHeader(),
        iterateInstance: this.subscriptions,
        getDurationTime: currentDurationTime,
      });
      this.subscriptionMatrices = matrices;
      this.filteredSubscriptionMatrices = matrices.slice(0, 5);
    },
    async getServiceLevelTopAlert(currentDurationTime) {
      const topAlertPerService = await this.getTopAlertsPerService({
        duration: currentDurationTime,
        condition: {
          entity: {
            scope: 'All',
          },
          capability: 'C',
          subType: this.currentSubType,
        },
      });

      this.alertTableItems =
        topAlertPerService.data.getTopAlertsPerService.alertsApps.map(
          (data) => ({
            subscriptionId: data.serviceName,
            alerts: data.count,
          }),
        );
    },
    async watchGetDurationTime(currentDurationTime) {
      try {
        this.isLoading = true;

        const services = await this.fetchAwsServices({
          duration: this.getDurationTime,
          serviceReq: { capability: 'C', subType: this.currentSubType },
          permissions: [permissionTypes.READ, permissionTypes.WRITE],
        });

        this.subscriptions = services.map((service) => ({
          ...service,
          selected: false,
        }));

        const promises = [
          this.getMatricesData(currentDurationTime),
          this.getServiceLevelTopAlert(currentDurationTime),
        ];

        await Promise.allSettled(promises);

        this.isLoading = false;
      } catch (error) {
        console.error('Error in getDurationTime watcher:', error);
        // Optionally, handle or log the error further as needed
      }
    },
    async getTopAlertsByMetrices(subscriptions) {
      const alerts = [];
      for (let subscription of subscriptions) {
        const topAlertsByMetircs = await this.getTopAlertsPerInstances({
          condition: {
            entity: {
              scope: 'Service',
              serviceName: subscription.key,
            },
          },
          duration: this.getDurationTime,
        });
        const matrices =
          topAlertsByMetircs?.data?.getTopMetricsByAlerts?.values;

        const subscriptionMetrics = {};

        for (let matric of matrices) {
          subscriptionMetrics[matric.metricName] = matric.alertCount;
        }

        alerts.push(subscriptionMetrics);
      }

      let totalAlerts = {};

      // const xyz = this.getAlertMatricNameMapper
      for (let individualMetrics in this.getAlertMatricNameMapper) {
        totalAlerts[individualMetrics] = alerts.reduce(
          (currentAlert, accumulator) => {
            if (!accumulator[individualMetrics]) return currentAlert;
            return currentAlert + accumulator[individualMetrics];
          },
          0,
        );
      }

      this.totalAlertsData = Object.entries(totalAlerts)
        .map(([key, value]) => ({
          alerts: value,
          metrics: this.$t(
            `aws.alertsTable.${this.getAlertMatricNameMapper[key]}`,
          ),
        }))
        .filter((alert) => alert.alerts);
    },
  },
  async beforeMount() {
    await this.getInitialData({ isInitialRender: true });
  },

  watch: {
    filteredSubscriptionMatrices: {
      handler: async function (currentMatrices) {
        this.handleFilterSubscriptionMatrices(currentMatrices);
      },
    },

    getDurationTime: {
      handler: async function (currentDurationTime) {
        this.watchGetDurationTime(currentDurationTime);
      },
    },
  },

  mounted() {
    this.setCurrentPage('overview');
  },
  computed: {
    ...mapGetters('globalState/settings', ['getDurationTime', 'intervalTime']),
    ...mapGetters('cloudState/detail', ['getCurrentServices']),
    ...mapState('cloudState/detail', ['currentSubType']),
    getAlertMatricNameMapper() {
      return alertsMapper;
    },
    alertTableToggleOptions() {
      return alertTableToggleOptions();
    },
    subscriptionOverviewHeader() {
      return [
        {
          key: 'subscriptionId',
          name: this.$t('aws.connectionName'),
        },
        {
          key: 'noOfDbInstance',
          name: this.$t('aws.rdsOverviewScreen.overviewTable.noOfDbInstances'),
        },
        ...subscriptionOverviewHeader(),
      ];
    },
  },
};
</script>
<style lang="scss">
.overview-page {
}
.overview-text {
  display: flex;
  margin-bottom: 1.25rem;
  color: $color-brand-accent;
}
.overview-tiles {
  display: flex;
  flex-direction: row;
  gap: 1.25rem;
  justify-content: space-between;
  margin-top: 1.25rem;
  margin-bottom: 1.25rem;
}

.line-separator {
  border: 0.03125em solid $line-separator;
  margin-top: 0.6875em;
  opacity: 80%;
  margin-bottom: 0.7em;
}
</style>
